<script setup lang="ts">
import { SupportChatMessage } from '@caks/shared/types/types.api'
import { computed } from 'vue'
import { IconCheck, IconDoubleCheck, IconDownload } from '@caks/uikit/icon'
import { isoToDateWithTime } from '@caks/shared/utils/timeHelpers'

interface Props {
  message: SupportChatMessage
  admin?: boolean
  desktop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  admin: false,
  desktop: false,
})

const readIcon = computed(() => {
  return props.message.read ? IconDoubleCheck : IconCheck
})
const mediaSize = ref('')

onMounted(async () => {
  if (props.message.media) {
    mediaSize.value = (await getMediaSize(props.message.media)) || ''
  }
})

const getMediaSize = async (link: string) => {
  try {
    const response = await fetch(link)
    const contentLength = response.headers.get('content-length') as string
    const fileSizeInBytes = parseInt(contentLength, 10)
    const fileSizeInKilobytes = fileSizeInBytes / 1024

    return fileSizeInKilobytes.toFixed(1)
  } catch (error) {
    console.error(error)
  }
}
</script>

<template>
  <div class="message-wrapper" :class="{ 'message-wrapper_user': message.from_user, admin }">
    <div class="message">
      <p v-if="message.text" class="message__text">{{ message.text }}</p>
      <div v-if="message.media" class="message__media media">
        <a :href="message.media" download target="_blank" class="media__button">
          <icon-download />
        </a>
        <p>{{ message.media }}</p>
        <a :href="message.media" download target="_blank">Скачать</a>
        <p>{{ mediaSize }} Kb</p>
      </div>
      <div class="message__status">
        <component v-if="!message.from_user && admin" :is="readIcon" class="icon-admin" />
        <component v-else-if="message.from_user && !admin" :is="readIcon" />
        <p class="message__date">{{ isoToDateWithTime(props.message.sent_at) }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.message-wrapper {
  padding: 0 16px;
  display: flex;
  justify-content: start;
  width: 100%;

  &_user {
    justify-content: end;

    .message {
      background-color: $white;
    }
  }
}

.message {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: $sub-green;
  width: 255px;

  &__status {
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 4px;

    .icon-admin {
      --svg-col: #ffffff;
    }
  }

  &__text {
    @include h5-2;
    color: $primary-text;

    overflow: hidden;
  }

  &__image {
  }

  &__date {
    @include h7-1;
    color: $sub-text;
    text-align: end;
  }

  .media {
    display: grid;
    grid-template-columns: 1fr repeat(2, 5fr);
    grid-template-rows: 1.5fr 1fr;
    grid-column-gap: 8px;
    grid-row-gap: 10px;
    width: 100%;

    :nth-child(3) {
      color: black !important;
    }
    :nth-child(4) {
      color: black !important;
    }

    color: black !important;

    :nth-child(1) {
      grid-area: 1 / 1 / 2 / 2;
    }
    :nth-child(2) {
      grid-area: 1 / 2 / 2 / 4;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    :nth-child(3) {
      grid-area: 2 / 2 / 3 / 3;

      @include h6-2;
      color: $ui-gray-1;
    }
    :nth-child(4) {
      grid-area: 2 / 3 / 3 / 4;

      @include h6-2;
      color: $ui-gray-1;
      text-align: end;
    }

    color: $white;
    @include h4-7;

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
    }
  }
}

.admin {
  justify-content: end;

  .message {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    border-radius: 8px;
    background-color: $primary-green;
    width: fit-content;
    max-width: 50%;

    &__text {
      @include h5-2;
      color: $white;
    }

    &__date {
      @include h7-1;
      color: $ui-gray-1;
      text-align: end;
    }
  }
}

.admin.message-wrapper_user {
  justify-content: start;

  .message {
    background-color: $ui-gray-1;

    &__text {
      color: $primary-text;
    }
    &__date {
      color: $sub-text;
    }
  }
}
</style>
